import React, { FunctionComponent, ReactElement, useContext, useEffect } from 'react';
import { HyruleToken } from '@nintendo/hyrule-react-commons';
import { useHistory, useLocation, useParams } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import { hasScope } from '@nintendo/hyrule-react-commons/lib/js/util/AuthUtils';
import HyruleTokenContext from './contexts/HyruleTokenContext';
import GuestOrder from './guest-order/GuestOrder';
import { SearchContext } from './contexts/SearchContext';
import Utils from '../shared/Utils';
import BlankStateScreen from './shared/components/nas-screens/BlankStateScreen';
import LoadingScreen from './shared/components/nas-screens/LoadingScreen';
import NoAccess from './shared/components/auth/NoAccess';
import { validDiceScopes } from '../shared/ScopeUtils';
import { ResolvedEntityType } from './shared/models/SharedEnums';
import Device from './device/Device';
import Account from './account/Account';

export const LEGACY_ROUTE_PREFIXES = ['account', 'device'];

const Home: FunctionComponent = (): ReactElement => {
    const searchContext = useContext(SearchContext);
    const token: HyruleToken = useContext(HyruleTokenContext);
    const history = useHistory();
    const location = useLocation();
    const { idParam } = useParams<{ idParam: string }>();

    const searchParam = new URLSearchParams(location.search).get('search') || '';
    useEffect(() => {
        if (idParam) {
            history.replace(`/?search=${encodeURIComponent(idParam)}`);
        }
    }, [idParam]);

    const naid = searchContext?.resolvedNaid || '';
    const email = searchContext?.resolvedEmail || '';
    const serialNumber = searchContext?.resolvedSerialNumber || '';
    const orderNumber = searchContext?.resolvedOrderNumber || '';

    const resolvedEntityType: ResolvedEntityType = (() => {
        if (searchContext?.isGuestOrder) return ResolvedEntityType.GuestOrder;
        if (serialNumber) return ResolvedEntityType.Device;
        if (naid || email) return ResolvedEntityType.Account;
        return ResolvedEntityType.None;
    })();

    const currentlyResolvedId = email || naid || serialNumber || orderNumber;

    const shouldPerformSearch: boolean =
        !!searchParam &&
        searchParam !== currentlyResolvedId &&
        (Utils.isValidEmailOrNaid(searchParam) ||
            Utils.isValidSerialNumber(searchParam) ||
            Utils.isValidOrderNumber(searchParam));

    useEffect(() => {
        if (shouldPerformSearch && token.accessToken) {
            searchContext?.performSearch(searchParam, history);
        }
    }, [searchParam, token.accessToken]);

    useEffect(() => {
        if (searchParam !== currentlyResolvedId && !shouldPerformSearch) {
            searchContext?.resetResolvedState();
        }
    }, [searchParam]);

    if (!hasScope(validDiceScopes)) {
        return <NoAccess />;
    }

    if (searchContext?.isNintendoAccountLoading) {
        return <LoadingScreen />;
    }

    switch (resolvedEntityType) {
        case ResolvedEntityType.None:
            return <BlankStateScreen />;
        case ResolvedEntityType.Device:
            return <Device />;
        case ResolvedEntityType.GuestOrder:
            return <GuestOrder />;
        default:
            return <Account />;
    }
};

export default Home;
